import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useRef, useCallback } from "react";
import Autoplay from "embla-carousel-autoplay";
import { useAuthCtx } from "@/lib/auth-context";
import {
  Sparkles,
  ArrowRight,
  CheckCircle2,
  TrendingUp,
  Target,
  Lightbulb,
  ShieldCheck,
  LineChart,
  Wallet as WalletIcon,
  Zap,
  Lock,
  Star,
  Quote,
  X as XIcon,
  Minus,
  FileSpreadsheet,
  Building2,
  Users,
  Heart,
  PieChart,
  Telescope,
  Bot,
} from "lucide-react";
import { Menu as MenuIcon } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { BackToTop } from "@/components/BackToTop";
import { PremiumDialogLazy } from "@/components/PremiumDialogLazy";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  type CarouselApi,
} from "@/components/ui/carousel";
import { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE, seo } from "@/lib/seo";
import { LandingSkeleton } from "@/components/skeletons";

const FAQ_ITEMS = [
  {
    q: "O Seu Bolso Tranquilo é realmente gratuito?",
    a: "Sim. O plano gratuito é grátis para sempre e inclui controle de receitas e gastos, categorização, dashboard, metas e acompanhamento de investimentos com limites. O Premium é opcional para quem quiser limites ilimitados, projeções avançadas e análise da carteira com IA.",
  },
  {
    q: "Meus dados financeiros estão seguros?",
    a: "Sim. Suas informações são armazenadas com criptografia em trânsito e regras estritas de acesso — somente você consegue ver os seus dados. Nunca vendemos ou compartilhamos suas informações.",
  },
  {
    q: "Preciso conectar minha conta bancária?",
    a: "Não. Você adiciona suas receitas e gastos manualmente, de forma rápida. Isso garante mais privacidade e controle total sobre o que entra na sua organização financeira.",
  },
  {
    q: "Funciona no celular?",
    a: "Sim. A interface é totalmente responsiva e funciona muito bem no celular, tablet e computador, sem precisar instalar nada.",
  },
  {
    q: "Quanto tempo leva para começar?",
    a: "Menos de 2 minutos. Você cria sua conta com e-mail, adiciona suas primeiras transações e já vê o dashboard funcionando.",
  },
  {
    q: "Posso cancelar o Premium quando quiser?",
    a: "Sim. O Premium não tem fidelidade — você pode cancelar a qualquer momento e continuar usando o plano gratuito normalmente.",
  },
  {
    q: "Vocês oferecem aconselhamento financeiro?",
    a: "Não. O Seu Bolso Tranquilo é uma ferramenta de organização e insights baseados nos seus próprios dados. As decisões financeiras são sempre suas.",
  },
  {
    q: "Qual o melhor app de controle financeiro pessoal gratuito?",
    a: "O Seu Bolso Tranquilo é um app de controle financeiro pessoal gratuito, em português, que funciona no celular e no computador. Ele organiza suas receitas e despesas, categoriza automaticamente os gastos e mostra para onde seu dinheiro está indo, sem precisar conectar conta bancária.",
  },
  {
    q: "Como organizar as finanças pessoais com um aplicativo?",
    a: "Comece registrando todas as suas entradas (salário, freela, MEI) e saídas (contas, alimentação, lazer). O app categoriza, mostra gráficos claros e aponta onde você está gastando demais. Em poucas semanas você ganha clareza e começa a economizar de verdade.",
  },
  {
    q: "Funciona para quem é MEI, autônomo ou freelancer?",
    a: "Sim. O Seu Bolso Tranquilo é ideal para MEIs, autônomos, freelancers, famílias, casais e estudantes — qualquer pessoa que queira ter clareza sobre o próprio dinheiro, separar gastos fixos de variáveis e construir o hábito de poupar.",
  },
] as const;

const LOGO_URL = `${SITE_URL}/logo.png`;
const OG_IMAGE_URL = DEFAULT_OG_IMAGE;

export const Route = createFileRoute("/")({
  head: () => {
    const base = seo({
      path: "/",
      title: "Controle financeiro pessoal grátis — Seu Bolso Tranquilo",
      description:
        "Controle financeiro pessoal grátis: organize gastos, metas e investimentos em um só app. Melhor que planilha de controle financeiro — sem fórmulas e sem manutenção.",
      imageAlt: "Seu Bolso Tranquilo — app de controle financeiro pessoal",
    });
    return {
      meta: base.meta,
      links: base.links,
      scripts: [
        ...base.scripts,
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ_ITEMS.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: {
              "@type": "Answer",
              text: item.a,
            },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: SITE_NAME,
          url: SITE_URL,
          logo: LOGO_URL,
          description:
            "Aplicativo de controle financeiro pessoal com insights automáticos, gráficos claros e metas.",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: SITE_NAME,
          url: SITE_URL,
          inLanguage: "pt-BR",
          publisher: {
            "@type": "Organization",
            name: SITE_NAME,
            logo: {
              "@type": "ImageObject",
              url: LOGO_URL,
            },
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: SITE_NAME,
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web, Android, iOS",
          url: SITE_URL,
          image: OG_IMAGE_URL,
          description:
            "App de controle financeiro pessoal: organize gastos, defina metas, acompanhe investimentos e receba insights automáticos.",
          inLanguage: "pt-BR",
          offers: [
            {
              "@type": "Offer",
              name: "Plano gratuito",
              price: "0",
              priceCurrency: "BRL",
            },
            {
              "@type": "Offer",
              name: "Premium",
              price: "15",
              priceCurrency: "BRL",
              priceSpecification: {
                "@type": "UnitPriceSpecification",
                price: "15",
                priceCurrency: "BRL",
                billingDuration: "P1M",
                unitText: "MONTH",
              },
            },
          ],
        }),
      },
      ],
    };
  },
  component: LandingPage,
  pendingComponent: LandingSkeleton,
});

function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuthCtx();

  useEffect(() => {
    if (user) navigate({ to: "/dashboard" });
  }, [user, navigate]);

  const [testimonialApi, setTestimonialApi] = useState<CarouselApi | null>(null);
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [testimonialCount, setTestimonialCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!testimonialApi) return;
    const update = () => {
      setTestimonialCount(testimonialApi.scrollSnapList().length);
      setTestimonialIndex(testimonialApi.selectedScrollSnap());
    };
    update();
    testimonialApi.on("select", update);
    testimonialApi.on("reInit", update);
  }, [testimonialApi]);

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-clip">
      {/* NAV */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/70 border-b border-border/40">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <BrandLogo className="size-10" priority />
            <span className="font-display font-bold text-lg">Seu Bolso Tranquilo</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <a href="#solucao" className="hover:text-foreground transition-colors">Solução</a>
            <a href="#como-funciona" className="hover:text-foreground transition-colors">Como funciona</a>
            <a href="#preco" className="hover:text-foreground transition-colors">Preço</a>
            <Link to="/aprenda" className="hover:text-foreground transition-colors">Aprenda</Link>
            <Link to="/calculadoras" className="hover:text-foreground transition-colors">Calculadoras</Link>
            <Link to="/planilha-vs-app" className="hover:text-foreground transition-colors">Planilha vs App</Link>
            <a href="#faq" className="hover:text-foreground transition-colors">FAQ</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/auth" className="hidden sm:inline-flex text-sm font-medium text-muted-foreground hover:text-foreground px-3 py-2 transition-colors">
              Entrar
            </Link>
            <Link
              to="/auth"
              search={{ mode: "register" } as never}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-primary text-primary-foreground text-sm font-semibold shadow-glow hover:opacity-90 transition-opacity"
            >
              Começar grátis <ArrowRight className="size-3.5" />
            </Link>
            <button
              type="button"
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-label={mobileMenuOpen ? "Fechar menu" : "Abrir menu"}
              aria-expanded={mobileMenuOpen}
              aria-controls="landing-mobile-menu"
              className="md:hidden inline-flex items-center justify-center size-10 rounded-xl border border-border/60 text-foreground hover:bg-accent transition-colors"
            >
              {mobileMenuOpen ? <XIcon className="size-5" /> : <MenuIcon className="size-5" />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div
            id="landing-mobile-menu"
            className="md:hidden border-t border-border/40 bg-background/95 backdrop-blur-xl"
          >
            <nav className="max-w-7xl mx-auto px-5 py-3 flex flex-col text-sm">
              <Link
                to="/aprenda"
                onClick={() => setMobileMenuOpen(false)}
                className="py-2.5 text-foreground hover:text-primary transition-colors"
              >
                Aprenda
              </Link>
              <Link
                to="/calculadoras"
                onClick={() => setMobileMenuOpen(false)}
                className="py-2.5 text-foreground hover:text-primary transition-colors"
              >
                Calculadoras
              </Link>
              <Link
                to="/planilha-vs-app"
                onClick={() => setMobileMenuOpen(false)}
                className="py-2.5 text-foreground hover:text-primary transition-colors"
              >
                Planilha vs App
              </Link>
              <Link
                to="/auth"
                onClick={() => setMobileMenuOpen(false)}
                className="py-2.5 text-foreground hover:text-primary transition-colors sm:hidden"
              >
                Entrar
              </Link>
            </nav>
          </div>
        )}
      </header>

      <main>
      {/* HERO */}
      <section className="relative px-5 lg:px-8 pt-16 pb-24 lg:pt-28 lg:pb-32">
        <div className="absolute inset-0 bg-gradient-glow opacity-70 pointer-events-none" />
        <div className="absolute top-1/3 -left-24 size-[500px] rounded-full bg-primary/20 blur-3xl pointer-events-none" />
        <div className="absolute top-10 -right-24 size-[400px] rounded-full bg-primary-glow/15 blur-3xl pointer-events-none" />

        <div className="relative max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary/15 border border-primary/30 text-xs font-medium text-primary-glow mb-7">
            <Sparkles className="size-3.5" /> Comece hoje, em menos de 2 minutos
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold leading-[1.05] tracking-tight mb-6">
            Descubra para onde seu dinheiro está indo —{" "}
            <span className="text-gradient">e assuma o controle</span> em minutos.
          </h1>
          <p className="text-lg lg:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed mb-9">
            O jeito mais simples de organizar gastos, entender seus hábitos e finalmente conseguir guardar dinheiro.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-10">
            <Link
              to="/auth"
              search={{ mode: "register" } as never}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-4 rounded-2xl bg-gradient-primary text-primary-foreground font-semibold shadow-glow hover:opacity-90 transition-opacity text-base"
            >
              Começar grátis agora <ArrowRight className="size-4" />
            </Link>
            <Link
              to="/auth"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-4 rounded-2xl border border-border/60 hover:border-primary/40 hover:bg-surface-elevated transition-all text-sm font-medium"
            >
              Já tenho conta
            </Link>
          </div>
          <div className="flex items-center justify-center gap-3 text-sm text-muted-foreground">
            <div className="flex -space-x-2">
              {[
                "from-cat-shopping to-primary-glow",
                "from-cat-health to-success",
                "from-cat-bills to-warning",
                "from-cat-leisure to-primary",
              ].map((g, i) => (
                <div
                  key={i}
                  className={`size-7 rounded-full bg-gradient-to-br ${g} border-2 border-background`}
                />
              ))}
            </div>
            <span>+1.000 pessoas já organizando suas finanças</span>
          </div>
        </div>

        {/* Mock dashboard preview */}
        <div className="relative max-w-5xl mx-auto mt-16 lg:mt-20">
          <div className="absolute -inset-x-10 -inset-y-6 bg-gradient-primary opacity-20 blur-3xl rounded-[3rem] pointer-events-none" />
          <div className="relative rounded-3xl bg-gradient-card border border-border/40 shadow-elevated p-4 sm:p-6 backdrop-blur">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              {[
                { label: "Saldo em conta", value: "R$ 4.820", color: "text-primary-glow", icon: WalletIcon },
                { label: "Recebido no mês", value: "R$ 6.500", color: "text-success", icon: TrendingUp },
                { label: "Economizado", value: "R$ 1.250", color: "text-warning", icon: Target },
              ].map((s) => (
                <div key={s.label} className="rounded-2xl bg-surface-elevated/60 border border-border/40 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-muted-foreground">{s.label}</span>
                    <s.icon className={`size-4 ${s.color}`} />
                  </div>
                  <p className={`font-display font-bold text-2xl ${s.color} tabular-nums`}>{s.value}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 rounded-2xl bg-surface-elevated/40 border border-border/40 p-5 flex items-center gap-4">
              <div className="size-11 rounded-xl bg-primary/20 border border-primary/30 flex items-center justify-center">
                <Lightbulb className="size-5 text-primary-glow" />
              </div>
              <div>
                <p className="text-sm font-semibold">Insight da semana</p>
                <p className="text-sm text-muted-foreground">
                  Você gastou 32% menos com delivery. Continue assim!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DOR */}
      <section className="px-5 lg:px-8 py-20 lg:py-28 border-t border-border/40">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary-glow mb-3">Você se identifica?</p>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight">
              Se sente <span className="text-gradient">no aperto</span> todo mês?
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              "Chega no fim do mês sem saber para onde foi o dinheiro",
              "Tenta economizar, mas nunca sobra",
              "Já tentou planilhas e desistiu na primeira semana",
              "Sente que poderia estar bem melhor financeiramente",
            ].map((t) => (
              <div
                key={t}
                className="rounded-2xl bg-gradient-card border border-border/40 p-5 flex items-start gap-3"
              >
                <div className="size-6 rounded-full bg-destructive/15 border border-destructive/30 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-destructive text-sm">✕</span>
                </div>
                <p className="text-sm lg:text-base text-foreground/90">{t}</p>
              </div>
            ))}
          </div>
          <p className="text-center mt-10 text-lg text-muted-foreground">
            Você <span className="text-foreground font-semibold">não está sozinho</span> — e o problema não é você.
          </p>
        </div>
      </section>

      {/* SOLUÇÃO */}
      <section id="solucao" className="px-5 lg:px-8 py-20 lg:py-28 border-t border-border/40 relative">
        <div className="absolute inset-0 bg-gradient-glow opacity-30 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary-glow mb-3">A solução</p>
          <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight mb-5">
            Conheça o <span className="text-gradient">Seu Bolso Tranquilo</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-12">
            Uma plataforma simples e inteligente que te mostra exatamente o que está acontecendo com o seu dinheiro.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { t: "Quanto você ganha", i: TrendingUp, c: "text-success" },
              { t: "Quanto você gasta", i: WalletIcon, c: "text-warning" },
              { t: "Onde perde dinheiro", i: LineChart, c: "text-destructive" },
              { t: "Como melhorar", i: Lightbulb, c: "text-primary-glow" },
            ].map((item) => (
              <div key={item.t} className="rounded-2xl bg-gradient-card border border-border/40 p-6 text-left">
                <div className="size-10 rounded-xl bg-surface-elevated border border-border/40 flex items-center justify-center mb-4">
                  <item.i className={`size-5 ${item.c}`} />
                </div>
                <p className="font-semibold">{item.t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMO FUNCIONA */}
      <section id="como-funciona" className="px-5 lg:px-8 py-20 lg:py-28 border-t border-border/40">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary-glow mb-3">Como funciona</p>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight">
              Em <span className="text-gradient">3 passos simples</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                n: "01",
                t: "Registre seus dados",
                d: "Adicione sua renda e gastos rapidamente, sem complicação.",
              },
              {
                n: "02",
                t: "Veja seu diagnóstico",
                d: "Receba uma análise clara da sua situação financeira atual.",
              },
              {
                n: "03",
                t: "Melhore com insights",
                d: "Descubra onde economizar e como evoluir mês a mês.",
              },
            ].map((s) => (
              <div key={s.n} className="relative rounded-3xl bg-gradient-card border border-border/40 p-7">
                <div className="font-display text-5xl font-bold text-gradient mb-4">{s.n}</div>
                <h3 className="font-display text-xl font-semibold mb-2">{s.t}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INVESTIMENTOS */}
      <section className="px-5 lg:px-8 py-20 lg:py-28 border-t border-border/40 relative overflow-hidden">
        <div className="absolute top-1/2 -right-48 size-[480px] -translate-y-1/2 rounded-full bg-primary/15 blur-3xl pointer-events-none" />
        <div className="relative max-w-5xl mx-auto grid lg:grid-cols-[0.9fr_1.1fr] gap-12 items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary-glow mb-3">Seus investimentos</p>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight mb-5">
              Do orçamento ao patrimônio, <span className="text-gradient">tudo no mesmo lugar.</span>
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-7">
              Acompanhe renda fixa, ações e FIIs, atualize preços manualmente e veja quanto sua carteira vale hoje — junto do restante da sua vida financeira.
            </p>
            <ul className="space-y-3">
              {[
                "Patrimônio investido e rentabilidade em tempo real",
                "Distribuição da carteira por classe de ativo",
                "Renda fixa calculada com a taxa CDI configurada",
                "Projeções para 1, 5, 10 e 20 anos",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm">
                  <CheckCircle2 className="size-5 text-success shrink-0" />
                  <span className="text-foreground/90">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-3xl bg-gradient-card border border-primary/25 p-5 sm:p-7 shadow-elevated">
            <div className="flex items-center justify-between mb-7">
              <div>
                <p className="text-xs text-muted-foreground">Patrimônio investido</p>
                <p className="font-display text-3xl font-bold mt-1 tabular-nums">R$ 48.760</p>
              </div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-success/15 border border-success/30 px-3 py-1 text-xs font-semibold text-success">
                <TrendingUp className="size-3.5" /> +12,4%
              </span>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              {[
                { icon: PieChart, label: "Alocação", value: "3 classes" },
                { icon: LineChart, label: "Rentabilidade", value: "+ R$ 5.380" },
                { icon: Telescope, label: "Em 10 anos", value: "R$ 172 mil" },
              ].map((item) => (
                <div key={item.label} className="rounded-2xl bg-surface-elevated/55 border border-border/40 p-4">
                  <item.icon className="size-4 text-primary-glow mb-4" />
                  <p className="text-xs text-muted-foreground">{item.label}</p>
                  <p className="font-display font-semibold mt-1">{item.value}</p>
                </div>
              ))}
            </div>
            <div className="mt-3 rounded-2xl bg-primary/10 border border-primary/25 p-4 flex items-start gap-3">
              <Bot className="size-5 text-primary-glow shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold">Análise inteligente da carteira</p>
                <p className="text-xs text-muted-foreground mt-1">Entenda concentração, diversificação e progresso das suas metas com o Premium.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFÍCIOS */}
      <section className="px-5 lg:px-8 py-20 lg:py-28 border-t border-border/40">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary-glow mb-3">Benefícios</p>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight mb-5">
              Resultados que você <span className="text-gradient">vai sentir</span> no bolso.
            </h2>
            <p className="text-muted-foreground text-lg">
              Pequenas mudanças, grandes impactos. Em poucas semanas, você já enxerga a diferença.
            </p>
          </div>
          <ul className="space-y-3">
            {[
              "Clareza total sobre seu dinheiro",
              "Pare de viver no aperto todo mês",
              "Crie o hábito de economizar",
              "Atinja suas metas financeiras",
              "Tome decisões mais inteligentes",
            ].map((b) => (
              <li key={b} className="flex items-center gap-3 rounded-2xl bg-gradient-card border border-border/40 px-5 py-4">
                <CheckCircle2 className="size-5 text-success shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* DIFERENCIAL */}
      <section className="px-5 lg:px-8 py-20 lg:py-28 border-t border-border/40 relative">
        <div className="absolute inset-0 bg-gradient-glow opacity-30 pointer-events-none" />
        <div className="relative max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary-glow mb-3">Diferencial</p>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight mb-4">
              Não é só controle. É <span className="text-gradient">direção.</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              O Seu Bolso Tranquilo te diz o que fazer com insights automáticos e personalizados.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { i: "⚠️", t: "Você está gastando mais do que deveria com delivery." },
              { i: "📉", t: "Se continuar assim, pode faltar dinheiro no fim do mês." },
              { i: "💰", t: "Você pode economizar R$500/mês com pequenos ajustes." },
            ].map((c) => (
              <div key={c.t} className="rounded-2xl bg-gradient-card border border-primary/20 p-6 shadow-glow/30">
                <div className="text-2xl mb-3">{c.i}</div>
                <p className="text-sm leading-relaxed">{c.t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARATIVO */}
      <section id="comparativo" className="px-5 lg:px-8 py-20 lg:py-28 border-t border-border/40">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary-glow mb-3">Comparativo</p>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight mb-4">
              Por que o Seu Bolso Tranquilo é <span className="text-gradient">diferente</span>?
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Compare com planilhas tradicionais e com apps que pedem acesso à sua conta bancária.
            </p>
            <p className="mt-4 text-sm">
              <Link to="/planilha-vs-app" className="text-primary hover:underline font-medium">
                Veja o comparativo completo: planilha de controle financeiro x app
              </Link>
            </p>
          </div>

          {/* Mobile: cards empilhados */}
          <div className="grid gap-4 md:hidden">
            {[
              {
                name: "Seu Bolso Tranquilo",
                icon: Sparkles,
                highlight: true,
                tagline: "Simples, privado e inteligente",
                rows: [
                  ["Sem conectar conta bancária", true],
                  ["Insights automáticos com IA", true],
                  ["Funciona no celular e PC", true],
                  ["Categorização rápida", true],
                   ["Carteira de investimentos integrada", true],
                   ["Rentabilidade e projeções automáticas", true],
                  ["Plano gratuito de verdade", true],
                  ["Configuração em 2 minutos", true],
                ] as [string, boolean][],
              },
              {
                name: "Planilha",
                icon: FileSpreadsheet,
                highlight: false,
                tagline: "Trabalhosa e fácil de abandonar",
                rows: [
                  ["Sem conectar conta bancária", true],
                  ["Insights automáticos com IA", false],
                  ["Funciona no celular e PC", "partial"],
                  ["Categorização rápida", false],
                   ["Carteira de investimentos integrada", "partial"],
                   ["Rentabilidade e projeções automáticas", false],
                  ["Plano gratuito de verdade", true],
                  ["Configuração em 2 minutos", false],
                ] as [string, boolean | "partial"][],
              },
              {
                name: "Apps que conectam banco",
                icon: Building2,
                highlight: false,
                tagline: "Dependem do seu Open Finance",
                rows: [
                  ["Sem conectar conta bancária", false],
                  ["Insights automáticos com IA", "partial"],
                  ["Funciona no celular e PC", true],
                  ["Categorização rápida", true],
                   ["Carteira de investimentos integrada", "partial"],
                   ["Rentabilidade e projeções automáticas", "partial"],
                  ["Plano gratuito de verdade", "partial"],
                  ["Configuração em 2 minutos", false],
                ] as [string, boolean | "partial"][],
              },
            ].map((col) => (
              <div
                key={col.name}
                className={`rounded-2xl border p-5 ${
                  col.highlight
                    ? "bg-gradient-card border-primary/40 shadow-glow"
                    : "bg-gradient-card border-border/40"
                }`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className={`size-10 rounded-xl flex items-center justify-center ${col.highlight ? "bg-gradient-primary shadow-glow" : "bg-surface-elevated border border-border/40"}`}>
                    <col.icon className={`size-5 ${col.highlight ? "text-primary-foreground" : "text-muted-foreground"}`} />
                  </div>
                  <div>
                    <p className="font-display font-semibold text-base">{col.name}</p>
                    <p className="text-xs text-muted-foreground">{col.tagline}</p>
                  </div>
                </div>
                <ul className="space-y-2.5">
                  {col.rows.map(([label, val]) => (
                    <li key={label} className="flex items-center justify-between text-sm">
                      <span className="text-foreground/85">{label}</span>
                      {val === true ? (
                        <CheckCircle2 className="size-4 text-success shrink-0" />
                      ) : val === "partial" ? (
                        <Minus className="size-4 text-warning shrink-0" />
                      ) : (
                        <XIcon className="size-4 text-destructive shrink-0" />
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Desktop: tabela comparativa */}
          <div className="hidden md:block rounded-3xl bg-gradient-card border border-border/40 overflow-hidden">
            <div className="grid grid-cols-4 border-b border-border/40 bg-surface-elevated/40">
              <div className="p-5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Recurso
              </div>
              <div className="p-5 text-center relative bg-primary/5">
                <div className="absolute top-2 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-gradient-primary text-primary-foreground text-[10px] font-bold uppercase tracking-wider">
                  <Star className="size-2.5" /> Melhor escolha
                </div>
                <div className="size-10 mx-auto mt-3 rounded-xl bg-gradient-primary shadow-glow flex items-center justify-center mb-2">
                  <Sparkles className="size-5 text-primary-foreground" />
                </div>
                <p className="font-display font-semibold text-sm">Seu Bolso Tranquilo</p>
              </div>
              <div className="p-5 text-center">
                <div className="size-10 mx-auto rounded-xl bg-surface-elevated border border-border/40 flex items-center justify-center mb-2">
                  <FileSpreadsheet className="size-5 text-muted-foreground" />
                </div>
                <p className="font-display font-semibold text-sm text-muted-foreground">Planilha</p>
              </div>
              <div className="p-5 text-center">
                <div className="size-10 mx-auto rounded-xl bg-surface-elevated border border-border/40 flex items-center justify-center mb-2">
                  <Building2 className="size-5 text-muted-foreground" />
                </div>
                <p className="font-display font-semibold text-sm text-muted-foreground">Apps c/ banco</p>
              </div>
            </div>

            {[
              { label: "Sem conectar conta bancária", values: [true, true, false] },
              { label: "Privacidade total dos seus dados", values: [true, true, "partial"] },
              { label: "Insights automáticos com IA", values: [true, false, "partial"] },
              { label: "Categorização rápida e clara", values: [true, false, true] },
               { label: "Carteira de investimentos integrada", values: [true, "partial", "partial"] },
               { label: "Rentabilidade calculada automaticamente", values: [true, false, "partial"] },
               { label: "Projeções futuras de patrimônio", values: [true, false, "partial"] },
              { label: "Funciona no celular e no PC", values: [true, "partial", true] },
              { label: "Configuração em menos de 2 min", values: [true, false, false] },
              { label: "Sem fórmulas para manter", values: [true, false, true] },
              { label: "Plano gratuito real (não trial)", values: [true, true, "partial"] },
            ].map((row, idx) => (
              <div
                key={row.label}
                className={`grid grid-cols-4 items-center border-b border-border/30 last:border-b-0 ${
                  idx % 2 === 0 ? "bg-transparent" : "bg-surface-elevated/20"
                }`}
              >
                <div className="p-4 text-sm font-medium text-foreground/90">{row.label}</div>
                {row.values.map((v, i) => (
                  <div
                    key={i}
                    className={`p-4 flex items-center justify-center ${i === 0 ? "bg-primary/5" : ""}`}
                  >
                    {v === true ? (
                      <CheckCircle2 className={`size-5 ${i === 0 ? "text-primary-glow" : "text-success"}`} />
                    ) : v === "partial" ? (
                      <Minus className="size-5 text-warning" />
                    ) : (
                      <XIcon className="size-5 text-destructive/70" />
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="size-3.5 text-success" /> Tem</span>
            <span className="inline-flex items-center gap-1.5"><Minus className="size-3.5 text-warning" /> Parcial / depende</span>
            <span className="inline-flex items-center gap-1.5"><XIcon className="size-3.5 text-destructive/70" /> Não tem</span>
          </div>
        </div>
      </section>

      {/* PREÇO */}
      <section id="preco" className="px-5 lg:px-8 py-20 lg:py-28 border-t border-border/40">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary-glow mb-3">Preço</p>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight">
              Comece <span className="text-gradient">grátis</span>. Hoje.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5 max-w-3xl mx-auto">
            <div className="rounded-3xl bg-gradient-card border border-border/40 p-7">
              <p className="text-sm text-muted-foreground mb-2">Plano gratuito</p>
              <p className="font-display text-4xl font-bold mb-1">R$ 0</p>
              <p className="text-xs text-muted-foreground mb-6">Para sempre</p>
              <ul className="space-y-2.5 text-sm mb-8">
                {[
                  "Controle de receitas e gastos",
                  "Categorização automática",
                  "Dashboard simples",
                  "Até 30 transações por mês",
                  "Até 3 metas",
                  "Até 2 categorias personalizadas",
                   "Até 3 investimentos de renda fixa",
                   "Até 5 ações ou FIIs",
                   "Visão consolidada do patrimônio",
                ].map((f) => (
                  <li key={f} className="flex items-center gap-2"><CheckCircle2 className="size-4 text-success shrink-0" />{f}</li>
                ))}
              </ul>
              <Link
                to="/auth"
                search={{ mode: "register" } as never}
                className="block w-full text-center py-3 rounded-2xl border border-border/60 hover:border-primary/40 hover:bg-surface-elevated transition-all font-medium"
              >
                Começar grátis
              </Link>
            </div>
            <div className="relative rounded-3xl bg-gradient-card border-2 border-primary/40 p-7 shadow-glow">
              <div className="absolute -top-3 left-7 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-primary text-primary-foreground text-xs font-bold">
                <Star className="size-3" /> Recomendado
              </div>
              <p className="text-sm text-primary-glow mb-2">Premium</p>
              <p className="font-display text-4xl font-bold mb-1">R$ 15<span className="text-lg text-muted-foreground font-normal">/mês</span></p>
              <p className="text-xs text-muted-foreground mb-6">Cancele quando quiser</p>
              <ul className="space-y-2.5 text-sm mb-8">
                {[
                  "Tudo do plano gratuito",
                  "Transações ilimitadas",
                  "Metas ilimitadas",
                  "Categorias personalizadas ilimitadas",
                  "Insights avançados com IA",
                  "Previsões financeiras",
                   "Investimentos ilimitados",
                   "Histórico da evolução patrimonial",
                   "Projeções avançadas e independência financeira",
                   "Análise inteligente da carteira com IA",
                ].map((f) => (
                  <li key={f} className="flex items-center gap-2"><CheckCircle2 className="size-4 text-primary-glow shrink-0" />{f}</li>
                ))}
              </ul>
              <PremiumDialogLazy
                trigger={
                  <button
                    type="button"
                    className="block w-full text-center py-3 rounded-2xl bg-gradient-primary text-primary-foreground font-semibold shadow-glow hover:opacity-90 transition-opacity"
                  >
                    Assinar Premium
                  </button>
                }
              />
            </div>
          </div>
        </div>
      </section>

      {/* PROVA SOCIAL */}
      <section className="px-5 lg:px-8 py-20 lg:py-28 border-t border-border/40">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary-glow mb-3">Prova social</p>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight">
              Quem usa, <span className="text-gradient">recomenda</span>.
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto mt-4">
              Milhares de brasileiros já organizam o dinheiro com o Seu Bolso Tranquilo.
            </p>
          </div>

          {/* Stats bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-12">
            {[
              { icon: Users, value: "+1.000", label: "pessoas organizando o bolso" },
              { icon: Star, value: "4,9/5", label: "média de avaliação", stars: true },
              { icon: Heart, value: "94%", label: "recomendariam a um amigo" },
              { icon: TrendingUp, value: "R$ 380", label: "economia média no 1º mês" },
            ].map((s) => (
              <div
                key={s.label}
                className="rounded-2xl bg-gradient-card border border-border/40 p-5 text-center"
              >
                <div className="size-9 mx-auto rounded-xl bg-primary/15 border border-primary/30 flex items-center justify-center mb-3">
                  <s.icon className="size-4 text-primary-glow" />
                </div>
                <p className="font-display font-bold text-2xl text-gradient tabular-nums">{s.value}</p>
                {s.stars && (
                  <div className="flex justify-center gap-0.5 my-1">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className="size-3 fill-warning text-warning" />
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-1 leading-snug">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Depoimentos */}
          <Carousel
            setApi={setTestimonialApi}
            opts={{ align: "start", loop: true }}
            plugins={[Autoplay({ delay: 5000, stopOnInteraction: false })]}
            className="w-full"
            onMouseEnter={useCallback(() => {
              testimonialApi?.plugins()?.autoplay?.stop?.();
            }, [testimonialApi])}
            onMouseLeave={useCallback(() => {
              testimonialApi?.plugins()?.autoplay?.play?.();
            }, [testimonialApi])}
            onTouchStart={useCallback(() => {
              testimonialApi?.plugins()?.autoplay?.stop?.();
            }, [testimonialApi])}
            onTouchEnd={useCallback(() => {
              testimonialApi?.plugins()?.autoplay?.play?.();
            }, [testimonialApi])}
          >
            <CarouselContent className="-ml-5">
              {([
              {
                q: "Em 3 semanas eu vi pra onde tava indo meu salário. Cortei R$ 420 só de delivery e assinaturas que nem usava.",
                n: "Mariana Souza",
                role: "Designer • São Paulo, SP",
                grad: "from-cat-shopping to-primary-glow",
              },
              {
                q: "Já tentei 4 planilhas diferentes e sempre largava. Esse app eu uso todo dia, é rápido demais de lançar gasto.",
                n: "Rafael Tavares",
                role: "MEI • Belo Horizonte, MG",
                grad: "from-cat-health to-success",
              },
              {
                q: "Primeira vez na vida que consegui guardar dinheiro. Os insights me mostram coisas que eu nem percebia.",
                n: "Júlia Pereira",
                role: "Estudante • Recife, PE",
                grad: "from-cat-leisure to-primary",
              },
              {
                q: "Não quis ligar conta bancária e aqui não precisa. Tenho controle do que entra e sai sem entregar meus dados.",
                n: "Carlos Mendes",
                role: "Freelancer • Porto Alegre, RS",
                grad: "from-cat-bills to-warning",
              },
              {
                q: "Eu e meu marido usamos juntos pra ver o orçamento da casa. Acabou aquela briga de fim de mês sobre dinheiro.",
                n: "Patrícia Lima",
                role: "Mãe e administradora • Curitiba, PR",
                grad: "from-primary to-primary-glow",
              },
              {
                q: "Como autônomo a renda varia muito. A previsão financeira me ajudou a não estourar o cartão nos meses fracos.",
                n: "Diego Almeida",
                role: "Desenvolvedor • Florianópolis, SC",
                grad: "from-cat-health to-cat-leisure",
              },
              ] as const).map((t) => (
                <CarouselItem
                  key={t.n}
                  className="pl-5 basis-full md:basis-1/2 lg:basis-1/3"
                >
                  <div className="h-full rounded-2xl bg-gradient-card border border-border/40 p-6 flex flex-col">
                    <div className="flex items-center justify-between mb-3">
                      <Quote className="size-6 text-primary-glow/60" />
                      <div className="flex gap-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star key={i} className="size-3.5 fill-warning text-warning" />
                        ))}
                      </div>
                    </div>
                    <p className="text-foreground/90 mb-5 leading-relaxed text-sm flex-1">"{t.q}"</p>
                    <div className="flex items-center gap-3 pt-4 border-t border-border/30">
                      <div className={`size-10 rounded-full bg-gradient-to-br ${t.grad} flex items-center justify-center text-primary-foreground text-sm font-bold shrink-0`}>
                        {t.n.charAt(0)}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">{t.n}</p>
                        <p className="text-xs text-muted-foreground truncate">{t.role}</p>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden lg:flex -left-4 xl:-left-12 bg-gradient-card border-border/60 hover:bg-accent" />
            <CarouselNext className="hidden lg:flex -right-4 xl:-right-12 bg-gradient-card border-border/60 hover:bg-accent" />
          </Carousel>

          {/* Paginação por bolinhas */}
          {testimonialCount > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              {Array.from({ length: testimonialCount }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Ir para depoimento ${i + 1}`}
                  onClick={() => testimonialApi?.scrollTo(i)}
                  className={`h-6 rounded-full transition-all ${
                    i === testimonialIndex
                      ? "w-10 bg-primary-glow"
                      : "w-6 bg-border hover:bg-muted-foreground/60"
                  }`}
                />
              ))}
            </div>
          )}

          {/* Selo de confiança */}
          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <ShieldCheck className="size-4 text-success" />
              <span>Dados criptografados</span>
            </div>
            <div className="hidden sm:block size-1 rounded-full bg-border" />
            <div className="flex items-center gap-2 text-muted-foreground">
              <Lock className="size-4 text-primary-glow" />
              <span>Sem acesso ao seu banco</span>
            </div>
            <div className="hidden sm:block size-1 rounded-full bg-border" />
            <div className="flex items-center gap-2 text-muted-foreground">
              <CheckCircle2 className="size-4 text-success" />
              <span>Cancele quando quiser</span>
            </div>
          </div>

          {/* CTA pós prova social */}
          <div className="mt-14 relative overflow-hidden rounded-3xl border border-primary/30 bg-gradient-card p-8 lg:p-12 text-center">
            <div className="absolute inset-0 bg-gradient-primary opacity-10 pointer-events-none" />
            <div className="relative">
              <p className="text-xs font-semibold uppercase tracking-widest text-primary-glow mb-3">
                Sua vez
              </p>
              <h3 className="font-display text-2xl lg:text-4xl font-bold tracking-tight mb-4">
                Pronto pra dar o <span className="text-gradient">primeiro passo</span>?
              </h3>
              <p className="text-muted-foreground text-base lg:text-lg max-w-2xl mx-auto mb-7">
                Crie sua conta grátis em menos de 1 minuto e comece a organizar o seu dinheiro hoje mesmo. Sem cartão, sem complicação.
              </p>
              <Link
                to="/auth"
                className="inline-flex items-center gap-2 bg-gradient-primary text-primary-foreground font-semibold px-8 py-4 rounded-2xl shadow-glow hover:scale-[1.02] transition-transform"
              >
                Criar minha conta grátis
                <ArrowRight className="size-5" />
              </Link>
              <p className="text-xs text-muted-foreground mt-4">
                +1.000 brasileiros já organizam o bolso com a gente.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="px-5 lg:px-8 py-20 lg:py-28 border-t border-border/40">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary-glow mb-3">FAQ</p>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight">
              Perguntas <span className="text-gradient">frequentes</span>
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQ_ITEMS.map((item, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="rounded-2xl bg-gradient-card border border-border/40 px-5 data-[state=open]:border-primary/30 transition-colors"
              >
                <AccordionTrigger className="text-left text-base font-semibold hover:no-underline py-5">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pb-5">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="px-5 lg:px-8 py-20 lg:py-28 border-t border-border/40 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-primary opacity-15 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-glow opacity-50 pointer-events-none" />
        <div className="relative max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary/15 border border-primary/30 text-xs font-medium text-primary-glow mb-6">
            <Zap className="size-3.5" /> Comece hoje
          </div>
          <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight mb-5">
            Pronto para assumir o controle da sua <span className="text-gradient">vida financeira?</span>
          </h2>
          <p className="text-lg text-muted-foreground mb-9">
            Leva menos de 2 minutos. É grátis. E pode mudar tudo.
          </p>
          <Link
            to="/auth"
            search={{ mode: "register" } as never}
            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-gradient-primary text-primary-foreground font-semibold shadow-glow hover:opacity-90 transition-opacity text-base"
          >
            Criar minha conta <ArrowRight className="size-4" />
          </Link>
        </div>
      </section>

      {/* CONFIANÇA */}
      <section className="px-5 lg:px-8 py-14 border-t border-border/40">
        <div className="max-w-5xl mx-auto grid sm:grid-cols-3 gap-4 text-center">
          {[
            { i: Lock, t: "Seus dados são privados", d: "Criptografia de ponta a ponta" },
            { i: ShieldCheck, t: "Interface segura", d: "Autenticação protegida" },
            { i: Sparkles, t: "Sem complicação", d: "Pensado para qualquer pessoa" },
          ].map((c) => (
            <div key={c.t} className="flex flex-col items-center gap-2">
              <div className="size-10 rounded-xl bg-surface-elevated border border-border/40 flex items-center justify-center">
                <c.i className="size-5 text-primary-glow" />
              </div>
              <p className="font-semibold text-sm">{c.t}</p>
              <p className="text-xs text-muted-foreground">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PARA QUEM É */}
      <section className="px-5 lg:px-8 py-14 border-t border-border/40">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary-glow mb-3">Para quem é</p>
            <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">
              Um app de <span className="text-gradient">controle financeiro pessoal</span> para todo mundo
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
            {[
              "MEIs e autônomos que querem separar o pessoal do profissional",
              "Freelancers com renda variável que precisam planejar o mês",
              "Famílias e casais organizando o orçamento doméstico",
              "Estudantes começando a entender finanças pessoais",
              "Quem cansou de planilhas complicadas",
              "Quem quer sair do vermelho e começar a poupar",
            ].map((t) => (
              <div key={t} className="rounded-2xl bg-gradient-card border border-border/40 px-5 py-4 flex items-start gap-2.5">
                <CheckCircle2 className="size-4 text-success shrink-0 mt-0.5" />
                <span className="text-foreground/90 leading-relaxed">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      </main>

      {/* FOOTER */}
      <footer className="border-t border-border/40 px-5 lg:px-8 py-10">
        <div className="max-w-7xl mx-auto flex flex-col gap-6 text-sm text-muted-foreground">
          <p className="max-w-3xl text-xs leading-relaxed text-muted-foreground/80">
            Seu Bolso Tranquilo é um aplicativo brasileiro de controle financeiro pessoal e organização financeira.
            Acompanhe receitas e despesas, categorize gastos, defina metas, controle o orçamento doméstico e receba
            insights automáticos para entender para onde seu dinheiro está indo — pelo computador ou pelo celular,
            sem precisar conectar conta bancária.
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <BrandLogo className="size-8" alt="Logo Seu Bolso Tranquilo" />
            <span>Seu Bolso Tranquilo © 2026</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <Link to="/termos" className="hover:text-foreground transition-colors">Termos de uso</Link>
            <Link to="/privacidade" className="hover:text-foreground transition-colors">Política de privacidade</Link>
            <Link to="/reembolso" className="hover:text-foreground transition-colors">Política de reembolso</Link>
            <Link to="/aprenda" className="hover:text-foreground transition-colors">Aprenda</Link>
            <Link to="/contato" className="hover:text-foreground transition-colors">Contato</Link>
          </div>
          </div>
        </div>
      </footer>
      <BackToTop />
    </div>
  );
}